import { useTranslations } from '@/i18n/translations';
import { Button, Row, Stack } from '@leanin/ui';
import { CommentItem, type CommentItemView } from '../comments/index';
import { usePostCommentHandlers } from './use-post-comment-handlers';
import type { PostRemovalTarget } from './post-removal-targets';

const PREVIEW_LINES = 2;

type Props = {
  postId: string;
  comment: CommentItemView;
  /** The post's total comment count, replies included. */
  commentCount: number;
  /** Scopes @-mention suggestions when the preview comment is edited. */
  audienceGroupId?: string | undefined;
  canReact: boolean;
  canComment: boolean;
  /** Opens the card's full comments (inline panel or post dialog). */
  onViewMore: () => void;
  /** Opens the full comments with the reply composer under this comment. */
  onReply: (commentId: string) => void;
  /** The group a leader removes comments from (see PostComments). */
  removalGroup?: PostRemovalTarget;
};

// One comment under a feed card's engagement line: the thread's own
// CommentItem, so Like and its ⋯ menu (copy link, edit, delete, report,
// leader removal) work
// in place. Reply opens the full thread with the reply composer under this
// comment — the preview has no room for a reply thread of its own. The
// thread's author-first and @all mention lookups are skipped here so a feed
// page doesn't fire them for every card.
export function PostCommentPreview({
  postId,
  comment,
  commentCount,
  audienceGroupId,
  canReact,
  canComment,
  onViewMore,
  onReply,
  removalGroup,
}: Props) {
  const t = useTranslations('Feed');
  const handlers = usePostCommentHandlers({ postId, canReact, removalGroup });

  return (
    <Stack gap="sm">
      <CommentItem
        key={comment.id}
        comment={comment}
        {...handlers}
        bodyLines={PREVIEW_LINES}
        {...(canComment ? { onReplyClick: () => onReply(comment.id) } : {})}
        {...(audienceGroupId !== undefined ? { audienceGroupId } : {})}
      />
      {commentCount > 1 ? (
        <Row>
          {/* Muted text action, flush with the avatar; darkens on hover. */}
          <Button variant="linkQuiet" size="sm" onClick={onViewMore}>
            {t('comments.viewMore')}
          </Button>
        </Row>
      ) : null}
    </Stack>
  );
}
